import { useState, useRef, useEffect } from "react";
import { ArrowClockwise, X, Warning, Sliders, CaretDown, CaretRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Bloc Relances V4 — vue métier dominante.
 *
 * Hiérarchie :
 *  1. Relance courante (1 seule, lecture immédiate) :
 *     - Titre = action métier (verbe)
 *     - Sous-titre = objectif relationnel
 *     - Date + bouton "Marquer relancé"
 *     - Badge ACTIVE
 *  2. Section repliée "Modifier les autres dates" :
 *     - Accès aux 4 slots techniques (prospect / envoyer / banque / client)
 *     - Permet de reprendre la main quand le réel diverge du moteur
 *  3. État neutre si aucune relance prévue (pas d'alerte, ouvre le pli technique)
 *
 * Backend V3 inchangé : slots = prospect / envoyer / banque / client + cycle client.
 */

// Mapping métier — calque exactement la logique backend `active_slot`
// (server.py:2047-2067) pour rester aligné sans divergence.
function computeActiveSlot(dossier) {
  if (dossier.status === "signe") return "client";
  if (!dossier.mandat_signe) return "prospect";
  if (dossier.date_envoi_banque || (dossier.etape_dates || {}).envoi_banque) return "banque";
  return "envoyer";
}

function meetingForSlot(dossier, slot) {
  const client = dossier.client_name || dossier.nom || "Client";
  const banque = (dossier.banques || [])[0];

  if (slot === "client") {
    const step = dossier.relance_client_step || "satisfaction";
    if (step === "satisfaction") {
      return { action: "Appeler le client", objective: "Enquête satisfaction post-signature" };
    }
    if (step === "recommandation") {
      return { action: "Demander une recommandation", objective: "Suivi M+4 — demande de recommandation" };
    }
    return { action: "Suivi patrimonial", objective: "Reprise patrimoniale M+10" };
  }

  if (slot === "banque") {
    const etape = dossier.etape;
    const where = banque ? `${banque}` : "la banque";
    let objective = `Suivre l'avancement avec ${where}`;
    if (etape === "envoi_banque") objective = `Confirmer la réception du dossier chez ${where}`;
    else if (etape === "accord_banque") objective = `Suivre l'accord de principe ${where}`;
    else if (etape === "accord_risque") objective = `Suivre l'accord risque ${where}`;
    else if (etape === "offre_editee") objective = `Faire signer l'offre éditée par ${client}`;
    return { action: `Relancer ${where}`, objective };
  }

  if (slot === "envoyer") {
    return { action: "Envoyer le dossier en banque", objective: `Mandat signé — envoyer le dossier ${client}` };
  }

  // prospect
  return { action: "Appeler le prospect", objective: `Qualifier ${client} et signer le mandat` };
}

const TECHNICAL_SLOTS = [
  { key: "prospect", label: "Relance prospect", rule: "J+1 / J+7 / J+15 selon type contact" },
  { key: "envoyer", label: "Relance « à envoyer »", rule: "J+1 après mandat signé" },
  { key: "banque", label: "Relance banque", rule: "J+2 envoi · J+7 accord · J+10 offre" },
  { key: "client", label: "Relance client (post-signature)", rule: "J+7 satisfaction · M+4 recommandation · M+10 patrimonial" },
];

function fmtFR(iso) {
  if (!iso) return null;
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return iso; }
}

function daysFromToday(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((d - today) / 86_400_000);
  } catch { return null; }
}

function EditableDate({ value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      try { inputRef.current.showPicker?.(); } catch { /* noop Safari < 16.4 */ }
    }
  }, [editing]);

  // Click-outside robuste (remplace onBlur trop précoce sur Mac/Safari)
  useEffect(() => {
    if (!editing) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setEditing(false);
      }
    };
    const t = setTimeout(() => document.addEventListener("mousedown", handler), 150);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", handler);
    };
  }, [editing]);

  if (editing) {
    return (
      <span ref={wrapperRef} className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          type="date"
          defaultValue={value || ""}
          onChange={(e) => { onSave(e.target.value); setEditing(false); }}
          className="text-sm font-semibold border border-[color:var(--cf-primary)] rounded px-1.5 py-0.5 text-[color:var(--cf-primary)] bg-white outline-none"
          data-testid={testid}
        />
        {value && (
          <button
            type="button"
            onClick={() => { onSave(null); setEditing(false); }}
            className="w-5 h-5 rounded flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-600"
            title="Effacer"
          >
            <X size={11} weight="bold" />
          </button>
        )}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="text-sm font-semibold text-[color:var(--cf-primary)] hover:bg-blue-50 rounded px-1.5 py-0.5"
      data-testid={testid}
    >
      {value ? fmtFR(value) : "définir"}
    </button>
  );
}

export default function RelancesBlock({ dossier, onUpdated }) {
  const [techOpen, setTechOpen] = useState(false);

  const save = async (slot, payload) => {
    try {
      const res = await api.patch(`/dossiers/${dossier.id}/relance-slot`, { slot, ...payload });
      onUpdated?.(res.data);
      toast.success("Mis à jour");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const markRelance = async () => {
    try {
      const res = await api.post(`/dossiers/${dossier.id}/relance`);
      onUpdated?.(res.data);
      toast.success("Relancé — prochaine relance recalculée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const activeSlot = computeActiveSlot(dossier);
  const activeDate = dossier[`relance_${activeSlot}_date`];
  const diff = daysFromToday(activeDate);
  const isOverdue = diff !== null && diff < 0;
  const isDue = diff !== null && diff === 0;
  const hasActive = !!activeDate && dossier.status !== "perdu";

  const { action, objective } = meetingForSlot(dossier, activeSlot);

  // Status badge
  let dateLabel = null;
  let dateTone = "calm"; // calm | due | late
  if (activeDate) {
    if (isOverdue) {
      dateLabel = `En retard ${-diff}j`;
      dateTone = "late";
    } else if (isDue) {
      dateLabel = "Aujourd'hui";
      dateTone = "due";
    } else {
      dateLabel = fmtFR(activeDate);
      dateTone = "calm";
    }
  }

  const toneStyles = {
    calm: { bg: "var(--cf-bg-2)", color: "var(--cf-neutral-text)", border: "transparent" },
    due:  { bg: "var(--cf-warning-soft)", color: "var(--cf-warning-text)", border: "var(--cf-warning-border)" },
    late: { bg: "var(--cf-danger-soft)", color: "var(--cf-danger-text)", border: "var(--cf-danger-border)" },
  };

  return (
    <div className="card-base p-5 md:p-6 mb-4" data-testid="relances-card">
      <h2 className="font-display text-lg md:text-xl font-extrabold text-slate-900 mb-3">
        Relances
      </h2>

      {/* === Relance dominante === */}
      {hasActive ? (
        <div
          className="rounded-xl border p-4 mb-3"
          style={{ background: "white", borderColor: dateTone === "late" ? "var(--cf-danger-border)" : "var(--cf-border)" }}
          data-testid="relance-active-card"
          data-slot={activeSlot}
        >
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className="text-[9px] font-bold text-[color:var(--cf-primary)] uppercase tracking-widest bg-[color:var(--cf-primary-soft)] px-1.5 py-0.5 rounded">
                  Active
                </span>
                {dateLabel && (
                  <span
                    className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                    style={{
                      background: toneStyles[dateTone].bg,
                      color: toneStyles[dateTone].color,
                      border: `1px solid ${toneStyles[dateTone].border}`,
                    }}
                    data-testid="relance-active-status"
                  >
                    {dateTone === "late" && <Warning size={9} weight="fill" className="inline mr-1 -mt-0.5" />}
                    {dateLabel}
                  </span>
                )}
              </div>
              <h3 className="font-display text-base md:text-lg font-extrabold text-slate-900 leading-tight" data-testid="relance-active-action">
                {action}
              </h3>
              <p className="text-[12.5px] text-slate-500 mt-1 leading-snug" data-testid="relance-active-objective">
                {objective}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-100">
            <div className="text-[11.5px] text-slate-500 flex items-center gap-1.5">
              <span className="font-semibold">Date prévue :</span>
              <EditableDate
                value={activeDate}
                onSave={(v) => save(activeSlot, { date: v })}
                testid="relance-active-date-input"
              />
            </div>
            {dossier.status === "en_cours" || dossier.status === "signe" ? (
              <button
                type="button"
                onClick={markRelance}
                className="px-4 py-2 rounded-xl bg-[color:var(--cf-primary)] text-white font-bold text-[13px] flex items-center gap-1.5 shadow-[0_4px_12px_rgba(45,110,255,0.25)] hover:bg-[color:var(--cf-primary-hover)] transition-colors"
                data-testid="mark-relance-btn"
              >
                <ArrowClockwise size={14} weight="bold" /> Marquer relancé
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div
          className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 p-4 mb-3 text-center"
          data-testid="relance-neutral"
        >
          <p className="text-[13px] font-semibold text-slate-500">Aucune action de relance prévue</p>
          <p className="text-[11.5px] text-slate-400 mt-0.5">
            Le moteur ne propose rien aujourd'hui. Tu peux forcer une date manuelle ci-dessous.
          </p>
        </div>
      )}

      {dossier.last_relance_date && (
        <div className="text-[11px] text-slate-400 mb-2 text-center" data-testid="last-relance-info">
          Dernière relance : {fmtFR(dossier.last_relance_date) || dossier.last_relance_date}
        </div>
      )}

      {/* === Section technique repliable === */}
      <button
        type="button"
        onClick={() => setTechOpen((v) => !v)}
        className="w-full mt-1 flex items-center justify-between px-3 py-2 rounded-lg text-[12px] font-bold text-slate-600 hover:bg-slate-50"
        data-testid="relance-tech-toggle"
        aria-expanded={techOpen}
      >
        <span className="flex items-center gap-1.5">
          <Sliders size={13} weight="bold" />
          Modifier les autres dates
        </span>
        {techOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
      </button>

      {techOpen && (
        <div className="mt-2 divide-y divide-slate-100 border-t border-slate-100 pt-2" data-testid="relance-tech-panel">
          {TECHNICAL_SLOTS.map((slot) => {
            const date = dossier[`relance_${slot.key}_date`];
            const auto = dossier[`relance_${slot.key}_auto`];
            const isActive = slot.key === activeSlot;
            return (
              <div
                key={slot.key}
                className="flex items-start justify-between py-2.5 gap-3"
                data-testid={`relance-tech-row-${slot.key}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                      {slot.label}
                    </span>
                    {isActive && (
                      <span className="text-[9px] font-bold text-[color:var(--cf-primary)] uppercase tracking-wider bg-[color:var(--cf-primary-soft)] px-1.5 py-0.5 rounded">
                        active
                      </span>
                    )}
                  </div>
                  <div className="text-[10.5px] text-slate-400 mt-0.5">{slot.rule}</div>
                </div>
                <span className="inline-flex items-center gap-1.5 flex-shrink-0">
                  <EditableDate
                    value={date}
                    onSave={(v) => save(slot.key, { date: v })}
                    testid={`relance-${slot.key}-input`}
                  />
                  {date ? (
                    auto ? (
                      <span
                        className="text-[10px] font-bold text-slate-400 uppercase tracking-wider"
                        data-testid={`relance-${slot.key}-auto-badge`}
                      >
                        auto
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => save(slot.key, { recompute_auto: true })}
                        className="text-[10px] font-bold text-[color:var(--cf-primary)] hover:underline inline-flex items-center gap-0.5"
                        title="Revenir au calcul automatique"
                        data-testid={`relance-${slot.key}-reset-auto`}
                      >
                        <ArrowClockwise size={10} weight="bold" /> auto
                      </button>
                    )
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
